"use client";

// Богатая форма создания задачи (открывается из FAB «Задачи» вместо простой формы).
// Выглядит как панель деталей задачи, но:
//   • статус НЕ показывается (новая задача всегда «Новая»);
//   • из этапов заполняются только «Постановка» (её ячейки) и «Референсы».
// На submit: createTask → проставить ячейки Постановки (setTaskCell) → залить
// буферизованные референсы (файлы/ссылки). cell_id из каталога совпадает с тем,
// что вернёт задача (глобальный каталог этапов), поэтому setTaskCell по нему валиден.

import { useEffect, useMemo, useRef, useState } from "react";
import { Flag } from "lucide-react";
import { IconAttach, IconCheck, IconChevronDown, IconChevronRight, IconClose, IconLink, IconSend, IconSpinner } from "@/components/ui/icons";

import { api } from "@/lib/api";
import { useAuth } from "@/components/auth-provider";
import { Select } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import type { Department } from "@/lib/types";
import { PRIORITY_OPTIONS, prioColor, prioFill, prioLabel, type TaskPriority } from "@/lib/task-priority";
import { INPUT_CLS, TEXTAREA_CLS } from "@/components/ui/input";
import { Spinner } from "@/components/ui/states";


const toIsoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromIso = (s: string): Date | undefined => { if (!s) return undefined; const [y, m, d] = s.split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };

// Инициалы в цветном кружке (цвет — из имени).
function Avatar({ name, size = 16 }: { name: string; size?: number }) {
  const initials = (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  return (
    <span className="grid shrink-0 place-items-center rounded-full font-semibold text-white"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42), background: "var(--primary)" }}>{initials}</span>
  );
}

export function TaskCreatePanel({ users, onClose, onCreated, presetAssignee }: {
  users: { id: string; name: string }[];
  onClose: () => void;
  onCreated: () => void;
  /** Кого подставить исполнителем сразу. Кабинет ставит себя: там задачу
   *  заводят СЕБЕ, и выбирать исполнителя каждый раз — лишний шаг
   *  (02.09.2026). В «Команде» поле остаётся пустым: там ставят коллеге. */
  presetAssignee?: string;
}) {
  const { user } = useAuth();
  // chatUsers не содержит текущего пользователя (себе не пишут) — добавляем его,
  // чтобы можно было поставить задачу самому себе.
  const allUsers = useMemo(
    () => (user && !users.some((u) => u.id === user.id)
      ? [{ id: user.id, name: `${user.fullName} (я)` }, ...users]
      : users),
    [users, user],
  );
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  // ⚠️ ИСПОЛНИТЕЛЕЙ МОЖЕТ БЫТЬ НЕСКОЛЬКО (Максим, 01.09.2026). Выбор устроен как
  // участники события в календаре: чипы выбранных + поиск + список с галочками.
  // Второй способ выбирать людей заводить нельзя — разъедутся.
  const [assignees, setAssignees] = useState<string[]>(presetAssignee ? [presetAssignee] : []);
  const [pq, setPq] = useState("");
  const toggleA = (id: string) => setAssignees((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));
  const [priority, setPriority] = useState<TaskPriority>("");
  const [due, setDue] = useState("");
  const [dept, setDept] = useState("");
  const [cFiles, setCFiles] = useState<File[]>([]);
  const [links, setLinks] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [refsOpen, setRefsOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const [depts, setDepts] = useState<Department[]>([]);
  useEffect(() => { api.getDepartments().then(setDepts).catch(() => {}); }, []);

  const selectableDepts = depts.filter((d) => d.title !== "Founder" && d.key !== "founder");
  const deptOptions = [
    ...(dept && !selectableDepts.some((d) => d.title === dept) ? [{ value: dept, label: dept }] : []),
    ...selectableDepts.map((d) => ({ value: d.title, label: d.title })),
  ];

  const inputCls = INPUT_CLS;


  async function submit() {
    if (!title.trim() || assignees.length === 0 || saving) return;
    setSaving(true);
    try {
      const task = await api.createTask({
        title: title.trim(), description: desc.trim(), assignee_id: assignees[0], assignee_ids: assignees,
        // Отдел — категория задачи (чип на карточке). Раньше он же уходил в
        // ячейку этапа «Постановка»; этапов в портале больше нет.
        category: dept,
        priority, due_date: due,
      });
      // Референсы
      for (const f of cFiles) await api.uploadTeamFile("task", task.id, f);
      for (const url of links) if (url.trim()) await api.addTeamLink("task", task.id, url.trim());
      onCreated();
      onClose();
    } finally { setSaving(false); }
  }

  const выбранные = allUsers.filter((u) => assignees.includes(u.id));
  const найденные = allUsers.filter(
    (u) => !assignees.includes(u.id) && u.name.toLowerCase().includes(pq.trim().toLowerCase()),
  );

  return (
    <div className="mb-3 flex max-h-[68dvh] w-full flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surfaceElevated)] shadow-[0_4px_24px_rgba(0,0,0,0.18)]">
      <input ref={fileRef} type="file" multiple className="hidden"
        onChange={(e) => { const fs = Array.from(e.target.files ?? []); setCFiles((p) => [...p, ...fs]); setRefsOpen(true); if (fileRef.current) fileRef.current.value = ""; }} />
        {/* Шапка: приоритет чипом (без статуса), название, чипы исполнитель/срок/отдел */}
        <div className="border-b border-[var(--border)] p-3">
          <div className="mb-2 flex items-center gap-1.5">
            <span className="relative inline-flex items-center gap-1 rounded-[var(--radius-md)] px-2 py-0.5 text-[11px] font-semibold"
              style={{ color: prioColor(priority), background: prioFill(priority) }}>
              <Flag size={10} /> {prioLabel(priority)}
              <Select
                value={priority}
                onChange={(v) => setPriority(v as TaskPriority)}
                size="xs"
                options={PRIORITY_OPTIONS}
                trigger={<button type="button" className="absolute inset-0 cursor-pointer opacity-0" aria-label="Приоритет" />}
              />
            </span>
            <span className="text-[11px] font-semibold text-[var(--textMuted)]">Новая задача</span>
            <span className="flex-1" />
            <button type="button" onClick={onClose} className="shrink-0 rounded-[var(--radius-md)] p-1 text-[var(--textMuted)] hover:text-[var(--text)]"><IconClose size={16} /></button>
          </div>
          <input className={`${inputCls} mb-2 text-[16px] font-bold`} placeholder="Название задачи *" autoFocus
             value={title} onChange={(e) => setTitle(e.target.value)} />
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--textMuted)]">
            {/* Исполнители: выбранные — чипами, добавление — из поповера со списком. */}
            {выбранные.map((u) => (
              <button type="button" key={u.id} onClick={() => toggleA(u.id)} data-tooltip="Убрать"
                className="glass-flat inline-flex items-center gap-1 rounded-full bg-[var(--secondary)] py-0.5 pl-0.5 pr-1.5 text-[11px]">
                <Avatar name={u.name} size={16} />
                <span>{u.name}</span>
                <IconClose size={9} />
              </button>
            ))}
            <Popover>
              <PopoverTrigger asChild>
                <button type="button"
                  className="glass-flat inline-flex items-center rounded-full bg-[var(--secondary)] px-2 py-0.5 text-[11px] text-[var(--textMuted)] hover:text-[var(--text)]">
                  {assignees.length ? "+ исполнитель" : "исполнитель *"}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" sideOffset={4} className="w-[240px] p-2">
                <input className="field-sm w-full" placeholder="Поиск сотрудника…" value={pq}
                  onChange={(e) => setPq(e.target.value)} />
                <div className="mt-1.5 max-h-44 space-y-0.5 overflow-y-auto scrollbar-autohide">
                  {найденные.map((u) => (
                    <button type="button" key={u.id} onClick={() => toggleA(u.id)}
                      className="glass-flat flex w-full items-center gap-2 rounded-[var(--radius-md)] px-1.5 py-1 text-left text-[12px]">
                      <Avatar name={u.name} size={18} />
                      <span className="truncate">{u.name}</span>
                    </button>
                  ))}
                  {найденные.length === 0 && (
                    <div className="px-1.5 py-2 text-[11px] text-[var(--textMuted)]">Никого не нашлось</div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {/* Срок */}
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="inline-flex items-center rounded-[var(--radius-md)] bg-[var(--secondary)] px-2 py-0.5 text-[11px] text-[var(--textMuted)] hover:text-[var(--text)]">
                  {due ? `срок ${due.slice(8, 10)}.${due.slice(5, 7)}` : "+ срок"}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" sideOffset={4} className="w-auto p-0">
                <Calendar mode="single" selected={fromIso(due)} defaultMonth={fromIso(due)}
                  onSelect={(d) => setDue(d ? toIsoDate(d) : "")} />
                <div className="flex items-center justify-between border-t border-[var(--border)] px-3 py-2 text-[11px]">
                  <button type="button" onClick={() => setDue("")} className="text-[var(--textMuted)] hover:text-[var(--text)]">Очистить</button>
                  <button type="button" onClick={() => setDue(toIsoDate(new Date()))} className="font-semibold text-[var(--primary)]">Сегодня</button>
                </div>
              </PopoverContent>
            </Popover>
            {/* Отдел («Заказчик / отдел») */}
            <Select value={dept} onChange={setDept} options={deptOptions} placeholder="+ отдел"
              size="xs" className="w-auto rounded-full border-0 bg-[var(--secondary)] py-0.5" />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-autohide p-3">
          <label className="mb-3 flex flex-col gap-0.5 text-[11px] text-[var(--textMuted)]">Описание
            <textarea rows={2} className={`${TEXTAREA_CLS} min-h-[32px] resize-y`} placeholder="Описание задачи…"
              value={desc} onChange={(e) => setDesc(e.target.value)} />
          </label>

          {/* ⚠️ Блока «Этапы → Постановка» здесь БОЛЬШЕ НЕТ. Он тянул каталог
              этапов из модуля «IT-проекты», а модуль убрали 28.07.2026 вместе с
              таблицами: запрос отвечал 500, ошибку глушил `.catch`, и на месте
              блока вечно крутился скелет «Загружаем данные…» (Максим,
              02.09.2026: «что тут за загрузка идёт?»). Формат задач теперь
              другой — этапов в нём нет. */}
          <div className="flex flex-col gap-1">
            {/* Файлы задачи (в старом формате назывались «Референсы»). */}
            <div className="rounded-[var(--radius-md)]">
              <button type="button" onClick={() => setRefsOpen((v) => !v)}
                className="glass-flat flex w-full items-center gap-2 rounded-[var(--radius-md)] px-1 py-1 text-left hover:bg-[var(--hover)]">
                {refsOpen ? <IconChevronDown size={13} className="shrink-0 text-[var(--textMuted)]" /> : <IconChevronRight size={13} className="shrink-0 text-[var(--textMuted)]" />}
                <span className="text-[12px] font-semibold text-[var(--text)]">Файлы</span>
                <span className="flex-1" />
                <span className="shrink-0 text-[11px] tabular-nums text-[var(--textMuted)]">{cFiles.length + links.length}</span>
              </button>
              {refsOpen && (
                <div className="ml-4 border-l border-[var(--border)] pl-3 pb-1.5 pt-1">
                  <div className="flex flex-wrap gap-1.5">
                    {cFiles.map((f, i) => (
                      <span key={`f${i}`} className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] px-1.5 py-0.5 text-[11px] text-[var(--text)]">
                        <IconAttach size={10} className="shrink-0" /><span className="max-w-[120px] truncate">{f.name}</span>
                        <button type="button" onClick={() => setCFiles((p) => p.filter((_, j) => j !== i))} className="text-[var(--textMuted)] hover:text-[#ef4444]"><IconClose size={10} /></button>
                      </span>
                    ))}
                    {links.map((l, i) => (
                      <span key={`l${i}`} className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] px-1.5 py-0.5 text-[11px] text-[var(--text)]">
                        <IconLink size={10} className="shrink-0" /><span className="max-w-[120px] truncate">{l}</span>
                        <button type="button" onClick={() => setLinks((p) => p.filter((_, j) => j !== i))} className="text-[var(--textMuted)] hover:text-[#ef4444]"><IconClose size={10} /></button>
                      </span>
                    ))}
                  </div>
                  {cFiles.length + links.length === 0 && <div className="mb-1 text-[11px] text-[var(--textMuted)]">Файлов пока нет</div>}
                  <div className="mt-1.5 flex items-center gap-2">
                    <button type="button" onClick={() => fileRef.current?.click()}
                      className="inline-flex items-center gap-1 text-[11px] text-[var(--textMuted)] hover:text-[var(--primary)]"><IconAttach size={12} /> файл</button>
                    <button type="button" onClick={() => { const u = window.prompt("Ссылка-референс:"); if (u?.trim()) { setLinks((p) => [...p, u.trim()]); setRefsOpen(true); } }}
                      className="inline-flex items-center gap-1 text-[11px] text-[var(--textMuted)] hover:text-[var(--primary)]"><IconLink size={12} /> ссылка</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-[var(--border)] p-3">
          <button type="button" onClick={() => void submit()} disabled={saving || !title.trim() || assignees.length === 0}
            className="glass-btn-primary inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius-md)] bg-[var(--primary)] py-2 text-[12px] font-semibold text-[var(--primaryText)] disabled:opacity-50">
            {saving ? <Spinner size={15} /> : <IconSend size={15} />} Поставить задачу
          </button>
        </div>
    </div>
  );
}
